import { __assign, __read, __rest, __spread } from "tslib";
import { useMemo, useRef, useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import { combineProps, useControlled, useEventCallback, useKeyboardMoveFocus, useRefState, } from '../../../foundation';
import { DEFAULT_GET_OPTION_LABEL, downshiftComponentName, stringArrToRegExp, } from './DownshiftUtils';
var DOWNSHIFT_ID_TOKEN = 'rc-chip-';
/** @inner inner hook work with useDownshift */
export var useDownshiftTag = function (_a) {
    var downshiftId = _a.id, _b = _a.value, selectedItemsProp = _b === void 0 ? [] : _b, onSelectChange = _a.onChange, variant = _a.variant, _c = _a.getOptionLabel, getOptionLabel = _c === void 0 ? DEFAULT_GET_OPTION_LABEL : _c, onInputChangeProp = _a.onInputChange, wrapperRef = _a.wrapperRef, inputRef = _a.inputRef, freeSolo = _a.freeSolo, maxFreeSolo = _a.maxFreeSolo, keyToTags = _a.keyToTags, onMaxFreeSolo = _a.onMaxFreeSolo, multiple = _a.multiple, getStopCreateFreeSolo = _a.getStopCreateFreeSolo, onReset = _a.onReset;
    var isAutocomplete = variant === 'autocomplete';
    var isSelectedFromAutocompleteRef = useRef(false);
    var _d = __read(useControlled({
        controlled: selectedItemsProp,
        default: [],
        name: downshiftComponentName,
    }), 2), tags = _d[0], _setTags = _d[1];
    var _e = __read(useState(false), 2), focused = _e[0], setFocused = _e[1];
    var _f = __read(useRefState(0), 2), focusedIndexRef = _f[0], setFocusedIndex = _f[1];
    var freeSoloCount = useMemo(function () { return tags.filter(function (x) { return x.freeSolo; }).length; }, [tags]);
    var setTags = function (_selectedItems) {
        _setTags(_selectedItems);
        onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange(_selectedItems);
        if (isAutocomplete && _selectedItems.length === 1) {
            var result = getOptionLabel(_selectedItems[0]);
            onInputChangeProp === null || onInputChangeProp === void 0 ? void 0 : onInputChangeProp(result);
            isSelectedFromAutocompleteRef.current = true;
        }
    };
    var _focusTag = function (tagToFocus) {
        var _a;
        var textFieldElm = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current;
        var inputElm = inputRef.current;
        if (tagToFocus === -1) {
            inputElm === null || inputElm === void 0 ? void 0 : inputElm.focus();
        }
        else {
            (_a = textFieldElm === null || textFieldElm === void 0 ? void 0 : textFieldElm.querySelector("[data-tag-index=\"" + tagToFocus + "\"]")) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    var focusTag = useEventCallback(function (tagToFocus, focus) {
        if (focus === void 0) { focus = true; }
        if (focusedIndexRef.current === tagToFocus)
            return;
        setFocusedIndex(tagToFocus);
        if (focus)
            _focusTag(tagToFocus);
    });
    /** when return `true` mean add item success */
    var checkAndAddFreeSolo = function (_a, e) {
        var _b = _a === void 0 ? {} : _a, selectedItem = _b.selectedItem, value = _b.value;
        var _c;
        var isAddItem = false;
        var currentValue = value !== null && value !== void 0 ? value : (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.value;
        if (!(getStopCreateFreeSolo === null || getStopCreateFreeSolo === void 0 ? void 0 : getStopCreateFreeSolo()) &&
            !selectedItem &&
            freeSolo &&
            currentValue &&
            currentValue.length > 0) {
            if (freeSoloCount < maxFreeSolo) {
                var items = currentValue
                    .trim()
                    .split(stringArrToRegExp(keyToTags))
                    .filter(function (x) { return x.trim() !== ''; });
                var toLength = freeSoloCount + items.length;
                if (toLength > maxFreeSolo) {
                    items.splice(-(toLength - maxFreeSolo));
                    onMaxFreeSolo === null || onMaxFreeSolo === void 0 ? void 0 : onMaxFreeSolo(maxFreeSolo);
                }
                if (items.length > 0) {
                    setTags(__spread(tags, items.map(function (label) {
                        return ({
                            id: uniqueId(DOWNSHIFT_ID_TOKEN),
                            isSuggestion: false,
                            freeSolo: true,
                            label: label,
                        });
                    })));
                    isAddItem = true;
                }
            }
            else {
                onMaxFreeSolo === null || onMaxFreeSolo === void 0 ? void 0 : onMaxFreeSolo(maxFreeSolo);
            }
            onReset === null || onReset === void 0 ? void 0 : onReset(e);
        }
        return isAddItem;
    };
    var removeTag = function (selectedItem) {
        var selectedItemIndex = tags.indexOf(selectedItem);
        if (selectedItemIndex > -1) {
            setTags(__spread(tags.slice(0, selectedItemIndex), tags.slice(selectedItemIndex + 1)));
        }
    };
    var addTag = function (selectedItem) {
        // * change to uniqueId
        if (selectedItem.freeSolo) {
            if (freeSoloCount < maxFreeSolo) {
                selectedItem.id = uniqueId(DOWNSHIFT_ID_TOKEN);
            }
            else {
                onMaxFreeSolo === null || onMaxFreeSolo === void 0 ? void 0 : onMaxFreeSolo(maxFreeSolo);
                return;
            }
        }
        if (multiple) {
            setTags(__spread(tags, [selectedItem]));
        }
        else {
            setTags([selectedItem]);
        }
    };
    var handleTagKey = useKeyboardMoveFocus({
        options: tags,
        focusedIndexRef: focusedIndexRef,
        infinite: 'order',
        onFocusedIndexChange: function (event, toIndex) {
            focusTag(toIndex);
            event === null || event === void 0 ? void 0 : event.preventDefault();
        },
    }).onKeyFocusedIndexHandle;
    /**
     * All Get props methods
     */
    var getTagListBoxProps = function (props) {
        return __assign({}, props);
    };
    var getTagProps = function (_a) {
        var index = _a.index, selectedItem = _a.selectedItem, restTagProps = __rest(_a, ["index", "selectedItem"]);
        var tabIndex = 
        // * when tag is not focus
        !focused &&
            // * and is first item
            index === 0
            ? 0
            : -1;
        return combineProps({
            tabIndex: tabIndex,
            // TODO: wait for input can complete that.
            // role: 'option',
            role: 'button',
            key: downshiftId + "-selected-item-" + index,
            'data-tag-index': index,
            'data-item-last': index === tags.length - 1,
            onClick: function (e) {
                e.stopPropagation();
            },
            onBlur: function () { return setFocused(false); },
            onFocus: function () {
                focusTag(index);
                setFocused(true);
            },
            onKeyDown: function (e) {
                var focusedIndex = focusedIndexRef.current;
                switch (e.key) {
                    case 'Backspace':
                        {
                            var lengthBeforeDelete = tags.length;
                            focusTag(
                            // * when length is 1 mean that will be remove all
                            lengthBeforeDelete === 1
                                ? -1
                                : focusedIndex === 0
                                    ? 0
                                    : focusedIndex - 1);
                            removeTag(selectedItem);
                        }
                        break;
                    case 'Delete':
                        {
                            var lengthBeforeDelete = tags.length;
                            focusTag(
                            // * when length is 1 mean that will be remove all
                            lengthBeforeDelete === 1
                                ? -1
                                : focusedIndex === tags.length - 1
                                    ? focusedIndex - 1
                                    : focusedIndex);
                            removeTag(selectedItem);
                        }
                        break;
                    default:
                        handleTagKey(e);
                        break;
                }
            },
            onDelete: function (e) {
                var _a;
                // * left button clicked
                if (e.button === 0) {
                    removeTag(selectedItem);
                    setFocused(false);
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
            },
        }, restTagProps);
    };
    return {
        focused: focused,
        setFocused: setFocused,
        tags: tags,
        isSelectedFromAutocompleteRef: isSelectedFromAutocompleteRef,
        focusedIndex: focusedIndexRef.current,
        checkAndAddFreeSolo: checkAndAddFreeSolo,
        getTagListBoxProps: getTagListBoxProps,
        getTagProps: getTagProps,
        focusTag: focusTag,
        addTag: addTag,
        clearTags: function () {
            setTags([]);
        },
        blur: function () {
            if (focusedIndexRef.current !== -1) {
                focusTag(-1, false);
            }
            setFocused(false);
        },
        // reset: () => {
        //   setActiveIndex(-1);
        // },
        focusLast: function () {
            var currTagsLength = tags.length;
            if (currTagsLength > 0) {
                focusTag(currTagsLength - 1);
            }
        },
    };
};
